import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { DistributorResponse, DistributorUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper } from '../utils';
import { DistributorDetailsSectionHandler } from './details.section';
import { COMPANY_ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { SharedAddressSectionHandler } from '../shared/address.section';
import { DistributorCustomizeSectionHandler } from './customize.section';
import { DistributorSettingsSectionHandler } from './settings.section';
import { HttpClient } from '@angular/common/http';
import { SoftLimitSectionHandler } from '../shared/limits.section';
import { buildChartSectionGroup } from '../shared/chart.section';
import { OidcIssuersSectionHandler } from '../shared/oidc-issuers.section';
import { DistributorNotificationsSectionHandler } from './notifications.section';
import { RetentionSectionHandler } from '../shared/retention.section';
import { PasswordPolicySectionHandler } from '../shared/password-policy.section';

@RegisterEntityHandler('distributor')
@Injectable()
export class DistributorHandler extends EntityHandler<DistributorResponse> {

    constructor(
        admin: AdminEditorService,
        private httpClient: HttpClient
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'suspended', 'deleted'];
    counts = ['vendor', 'client', 'asset', 'device', 'companion-camera', 'user'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listDistributors/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.accounts.listDistributors(ownerId, offset, limit, sort, filter, this.counts.join(',')), 3).then(response => ({
            ...response,
            type: 'distributor',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.DISTRIBUTORS'),
        }));
    }

    getEntity(id: string): Promise<DistributorResponse> {
        return this.admin.getCached(`getDistributor/${id}`, api => api.accounts.getDistributor(id), 10);
    }

    createEntity(ownerId: string, entity: any): Promise<DistributorResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.accounts.createDistributor(entity), { _navigation: 'distributor' });
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.accounts.updateDistributor(entity.id, { state: state as any }), { _navigation: 'distributor' });
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {

        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.DISTRIBUTOR.COLUMNS');

        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', COMPANY_ICONS.distributor),
            grid.TextColumn('website', 'WEBSITE'),
            grid.StateColumn(this.states),
            ...this.counts.map(count => grid.NumberColumn(`counts.${count}`, count.replace(/-/g, '_').toUpperCase(), 60)),            
            grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
        ];

        return Promise.resolve(columns);

    }

    getEditorSections(ownerId: string, _entity: any): Promise<SectionHandlerGroup<DistributorResponse>[]> {
        const sections: SectionHandlerGroup<DistributorResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new DistributorDetailsSectionHandler(this.admin, ownerId),
                new DistributorCustomizeSectionHandler(this.admin, ownerId, this.httpClient),
                new SharedAddressSectionHandler(this.admin, ownerId, 'distributor'),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
            sections: [
                new DistributorSettingsSectionHandler(this.admin, ownerId),
                new DistributorNotificationsSectionHandler(this.admin),
                this.admin.user.owner.type === 'system' ? new PasswordPolicySectionHandler<DistributorResponse>(this.admin, ownerId, 'distributor') : null, 
                new OidcIssuersSectionHandler<DistributorResponse>(this.admin, ownerId, 'distributor'),
                this.admin.user.owner.type === 'system' && new SoftLimitSectionHandler<DistributorResponse>(this.admin, ownerId, 'distributor'),
                this.admin.user.owner.type === 'system' ? new RetentionSectionHandler<DistributorResponse>(this.admin, ownerId, 'distributor') : null,
            ].filter(x => x),
        }];

        sections.push(buildChartSectionGroup(this.admin, 'distributor', this.counts, ['7d', '30d', '60d', '90d']));

        return Promise.resolve(sections);

    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: DistributorUpdateRequest, sections: FormSectionHandler<DistributorResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'distributor',
            entity: {}, // set up some defaults if you want
            sections: [
                new DistributorDetailsSectionHandler(this.admin, ownerId),
            ],
        });
    }


}













